/**
 * Shared EMT layout: one top nav for all EMT pages (Dashboard, Performance, Account, Jobs, Community, etc.).
 * Renders <Outlet /> for the current route so nav stays consistent and never "sticks" to a reduced set.
 * When optional children are provided (e.g. Community), renders children instead of Outlet.
 * Mobile: nav links in a sheet drawer; desktop: full nav bar.
 */
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useAuthCtx } from '../context/AuthContext';
import { useUnreadCounts } from '../context/UnreadCountsContext';
import { getAuthSafe } from '../lib/firebase';
import { Button } from './ui/button';
import AppLogo from './AppLogo';
import SafeText from './SafeText';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Bell,
  Briefcase,
  GraduationCap,
  LogOut,
  Menu,
  MessageSquare,
  Send,
  Settings,
  Users,
  Wallet,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NavSettingsButton from './NavSettingsButton';
import OverflowNav from './nav/OverflowNav';
import EmailVerificationBanner from './EmailVerificationBanner';
import BackupPasswordPrompt from './BackupPasswordPrompt';
import EmtAssistant from './assistant/EmtAssistant';

function navClass(pathname: string, path: string): string {
  const active = pathname === path || (path !== '/dashboard' && pathname.startsWith(path));
  return active ? 'rr-nav-link rr-nav-link--active' : 'rr-nav-link';
}

interface EMTLayoutProps {
  children?: ReactNode;
}

export default function EMTLayout({ children }: EMTLayoutProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { user, profile } = useAuthCtx();
  const unreadCounts = useUnreadCounts();
  const showNotifBadge =
    unreadCounts.hydrated && unreadCounts.notificationsBadgeLive && unreadCounts.notifications > 0;
  const showMsgBadge =
    unreadCounts.hydrated && unreadCounts.messagesBadgeLive && unreadCounts.messages > 0;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userData = {
    name: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : user?.displayName || 'User',
    title: profile?.professional?.certifications?.[0] || 'EMT',
    avatar: profile?.photoURL || user?.photoURL || '',
  };

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/career-wallet', label: 'Career Wallet', icon: Wallet },
    { to: '/performance', label: 'Readiness' },
    { to: '/account', label: 'My Profile' },
    { to: '/jobs', label: 'Jobs', icon: Briefcase },
    { to: '/find-training', label: 'Find training', icon: GraduationCap },
    { to: '/community', label: 'Community', icon: Users },
    { to: '/feedback', label: 'Feedback', icon: Send },
  ];
  // Top bar, in priority order (the rest go into More when narrow). My Profile is in the account menu.
  const desktopNavLinks: Array<{ to: string; label: string; icon?: typeof Wallet; testId?: string }> = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/career-wallet', label: 'Career Wallet', icon: Wallet, testId: 'emt-nav-career-wallet' },
    { to: '/performance', label: 'Readiness' },
    { to: '/jobs', label: 'Jobs', icon: Briefcase },
    { to: '/find-training', label: 'Find training', icon: GraduationCap, testId: 'emt-nav-find-training' },
    { to: '/community', label: 'Community', icon: Users },
    { to: '/feedback', label: 'Feedback', icon: Send },
  ];

  async function handleSignOut() {
    try {
      await signOut(getAuthSafe());
      navigate('/');
    } catch (err) {
      console.warn('[EMTLayout] Logout failed:', err);
    }
  }

  return (
    <div className="min-h-screen rr-page-bg">
      <div className="rr-nav sticky top-0 z-50 overflow-visible">
        <div className="rr-nav-inner max-w-[1920px] min-w-0 w-full justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 overflow-visible">
              <div className="flex-shrink-0 flex items-center">
                <AppLogo clickable={true} />
              </div>
              {/* Desktop/tablet nav (phone uses drawer). The links get the whole bar up to 1920px; each one is
                  measured, the ones that fit stay in order and the rest move into a "More" menu, so a
                  link is never cut off. My Profile is in the account menu (top right), not the bar. */}
              <OverflowNav
                ariaLabel="Main"
                className="hidden md:flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden"
                isActive={(to) => pathname === to || pathname.startsWith(to + '/')}
                remeasureKey={pathname}
                items={desktopNavLinks.map(({ to, label, icon: Icon, testId }) => ({
                  to,
                  label,
                  node: (
                    <Button asChild variant="ghost" className={navClass(pathname, to)} data-testid={testId}>
                      <Link to={to}>
                        {Icon ? <Icon className="hidden 2xl:inline w-4 h-4 mr-2" /> : null}
                        {label}
                      </Link>
                    </Button>
                  ),
                }))}
              />
              {/* Mobile menu trigger */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden h-11 w-11 rounded-lg min-h-11 min-w-11" aria-label="Open menu">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] max-w-[320px] flex flex-col p-0">
                  <SheetHeader className="p-4 border-b border-[var(--border-default)]">
                    <SheetTitle className="text-left">Menu</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col p-2 flex-1 overflow-y-auto">
                    {navLinks.map(({ to, label, icon: Icon }) => (
                      <Button key={to} asChild variant="ghost" className={`w-full justify-start ${navClass(pathname, to)} h-11 px-4`}>
                        <Link to={to} onClick={() => setMobileMenuOpen(false)}>
                          {Icon ? <Icon className="w-4 h-4 mr-3" /> : null}
                          {label}
                        </Link>
                      </Button>
                    ))}
                    <Button asChild variant="ghost" className={`w-full justify-start ${navClass(pathname, '/settings')} h-11 px-4`}>
                      <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start h-11 px-4 text-[var(--text-brand)] hover:text-[var(--rr-red-hover)] hover:bg-[var(--rr-red-light)] mt-2"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        void handleSignOut();
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Log out
                    </Button>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2.5 sm:gap-3">
              <Button asChild variant="outline" size="icon" className="rr-nav-icon-btn relative border-0 shadow-none" aria-label="Notifications">
                <Link
                  to="/notifications"
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    if (pathname === '/notifications') e.preventDefault();
                  }}
                >
                  <Bell className="w-4 h-4 rr-nav-icon" />
                  {showNotifBadge && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-xs flex items-center justify-center rounded-full">
                      {unreadCounts.notifications > 99 ? '99+' : unreadCounts.notifications}
                    </span>
                  )}
                </Link>
              </Button>
              <Button asChild variant="outline" size="icon" className="rr-nav-icon-btn relative border-0 shadow-none" aria-label="Messages">
                <Link to="/messages" title="Messages">
                  <MessageSquare className="w-4 h-4 rr-nav-icon" />
                  <span className="sr-only">Messages</span>
                  {showMsgBadge && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 bg-rose-500 text-white text-xs flex items-center justify-center rounded-full">
                      {unreadCounts.messages > 99 ? '99+' : unreadCounts.messages}
                    </span>
                  )}
                </Link>
              </Button>
              {/* At 390px the logo + hamburger (192px) and this four-button group (206px) do not fit
                  in the 358px row, and the group was painted over the hamburger — the Notifications
                  button intercepted its clicks, so the mobile menu could not be opened. Settings is
                  also in the sheet menu and the account dropdown, so the top-bar copy is the one to
                  drop below sm. */}
              <span className="hidden sm:inline-flex">
                <NavSettingsButton to="/settings" />
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex shrink-0 items-center gap-2 sm:gap-3 px-1.5 sm:px-3 py-2 rounded-lg hover:bg-[var(--surface-hover)] transition-colors text-left focus:outline-none focus:ring-2 focus:ring-[var(--rr-red)] focus:ring-offset-2 min-h-11 min-w-11 justify-center sm:justify-start sm:min-w-0"
                    aria-label="Account menu"
                  >
                    <Avatar className="h-9 w-9 flex-shrink-0">
                      <AvatarImage src={userData.avatar} />
                      <AvatarFallback className="rr-avatar text-sm border-0">
                        {(userData.name || 'User').split(' ').map((n) => n[0] || '').join('') || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden min-w-0 max-w-[11rem] flex-col justify-center gap-0.5 overflow-hidden text-left leading-tight lg:flex 2xl:max-w-[14rem]">
                      <p className="truncate text-sm font-medium text-[var(--text-primary)]"><SafeText value={userData.name} label="name" /></p>
                      <p className="truncate text-xs text-[var(--text-muted)]"><SafeText value={userData.title} label="title" /></p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium truncate">{userData.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/account">My Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="2xl:hidden">
                    <Link to="/feedback">Feedback</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => void handleSignOut()} className="text-rose-600 focus:text-rose-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
        </div>
      </div>
      <EmailVerificationBanner />
      <BackupPasswordPrompt />
      {children ?? (
        <div className="rr-page-enter">
          <Outlet />
        </div>
      )}
      {/* flag emt_assistant: the helper button in the corner of every EMT page (renders nothing when off). */}
      <EmtAssistant />
    </div>
  );
}
